/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Row,
  Col
} from 'react-bootstrap';
import SimplePhoneInput from './SimplePhoneInput';
import SimpleNotification from './SimpleNotification';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPlus, FaTimes } from 'react-icons/fa';

export default function AcceptedJobsSimple() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [diaryData, setDiaryData] = useState({
    name: '', email: '', phone: '', phone2: '', from: '', to: '',
    dateTime: new Date(), passengers: '', returnTrip: false,
    dog: false, disabled: false, notes: ''
  });
  const [showPhone2, setShowPhone2] = useState(false);
  const [diaryLoading, setDiaryLoading] = useState(false);
  const [msg, setMsg] = useState('');
  
  // Customer search functionality
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isContactAutoPopulated, setIsContactAutoPopulated] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    loadJobs();
  }, []);

  // Listen for diary refresh events
  useEffect(() => {
    const handleRefreshDiaryData = async (event) => {
      console.log('🔄 Refreshing diary data due to diary entry:', event.detail);
      
      try {
        setLoading(true);
        await loadJobs();
        console.log('✅ Diary data refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh diary data:', error);
        setError('Failed to refresh diary data');
      } finally {
        setLoading(false);
      }
    };

    // Add event listener
    window.addEventListener('refreshDiaryData', handleRefreshDiaryData);
    
    // Cleanup
    return () => {
      window.removeEventListener('refreshDiaryData', handleRefreshDiaryData);
    };
  }, []);

  const loadJobs = async () => {
    try {
      const me = localStorage.getItem('userId');
      const { data } = await axios.get('/view-leads');
      const myAccepted = data
        .filter(l => l.status === 'accepted' && l.acceptedBy?._id === me);

      // Also load available jobs in my monitored areas and list them in diary (highlighted)
      let availableWithin = [];
      try {
        const av = await axios.get('/job-monitor/available-jobs');
        availableWithin = (av.data?.withinArea || []).map(l => ({ ...l, __available: true }));
      } catch (e) {
        // non-fatal
      }

      const combined = [...myAccepted, ...availableWithin]
        .sort((a,b) => new Date(a.dateTime) - new Date(b.dateTime));

      setJobs(combined);
    } catch (err) {
      console.error(err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // ── Actions for pending (available) jobs ──
  const acceptLead = async (id) => {
    try {
      await axios.post(`/view-leads/accept/${id}`);
      await loadJobs();
      setMsg('Job accepted and added to My Diary');
    } catch (err) {
      console.error(err);
      setMsg('Failed to accept job');
    }
  };

  const declineLead = async (id) => {
    try {
      await axios.post(`/view-leads/decline/${id}`, { statusNote: 'Declined' });
      await loadJobs();
      setMsg('Job removed from list');
    } catch (err) {
      console.error(err);
      setMsg('Failed to decline job');
    }
  };

  const respondLead = async (id, note) => {
    try {
      await axios.post(`/view-leads/respond/${id}`, { statusNote: note });
      setMsg(`Sent: ${note}`);
    } catch (err) {
      console.error(err);
      setMsg('Failed to send message');
    }
  };

  const handleDiaryChange = e => {
    const { name, value, checked, type } = e.target;
    setDiaryData(d => ({ ...d, [name]: type === 'checkbox' ? checked : value }));
    
    // Check if this is the name field and starts with "#"
    if (name === 'name' && value.startsWith('#')) {
      const searchQuery = value.substring(1); // Remove the "#" prefix
      if (searchQuery.trim().length > 0) {
        searchCustomers(searchQuery);
      } else {
        setShowSearchResults(false);
        setSearchResults([]);
      }
    } else if (name === 'name') {
      // Hide search results if name doesn't start with "#"
      setShowSearchResults(false);
      setSearchResults([]);
      
      // Clear auto-populated flag if user manually changes name
      if (isContactAutoPopulated) {
        setIsContactAutoPopulated(false);
      }
    }
    
    // Search by phone when entered
    if (name === 'phone') {
      setIsContactAutoPopulated(false);
      
      // Debounced search by phone
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      if (value && value.trim().length >= 5) { // Search when at least 5 digits entered
        searchTimeoutRef.current = setTimeout(() => {
          searchByContact(value, null);
        }, 800); // Wait 800ms after user stops typing
      }
    }
    
    // Search by email when entered
    if (name === 'email') {
      setIsContactAutoPopulated(false);
      
      // Debounced search by email
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Check if email looks valid before searching
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && emailPattern.test(value)) {
        searchTimeoutRef.current = setTimeout(() => {
          searchByContact(null, value);
        }, 800); // Wait 800ms after user stops typing
      }
    }
    
    // Clear auto-populated flag if user manually changes from or to
    if (name === 'from' || name === 'to') {
      if (isContactAutoPopulated) {
        setIsContactAutoPopulated(false);
      }
    }
  };

  // Search customers function
  const searchCustomers = async (query) => {
    try {
      setSearchLoading(true);
      const { data } = await axios.get(`/diary/search-customers?query=${encodeURIComponent(query)}`);
      setSearchResults(data.customers || []);
      setShowSearchResults(data.customers && data.customers.length > 0);
    } catch (err) {
      console.error('Error searching customers:', err);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setSearchLoading(false);
    }
  };

  // Select customer from search results
  const selectCustomer = (customer) => {
    setDiaryData(d => ({
      ...d,
      name: customer.customerName,
      email: customer.customerEmail,
      phone: customer.customerPhone,
      from: customer.from,
      to: customer.to
    }));
    setShowSearchResults(false);
    setSearchResults([]);
  };

  // Clear Add Job form
  const clearAddJobForm = () => {
    setDiaryData({
      name: '', 
      email: '', 
      phone: '', 
      phone2: '',
      from: '', 
      to: '',
      dateTime: new Date(), 
      passengers: '', 
      returnTrip: false,
      dog: false, 
      disabled: false, 
      notes: ''
    });
    setShowPhone2(false);
    setIsContactAutoPopulated(false);
    setShowSearchResults(false);
    setSearchResults([]);
  };

  // Swap From and To fields
  const swapFromTo = () => {
    setDiaryData(d => ({
      ...d,
      from: d.to,
      to: d.from
    }));
  };

  // Search by exact phone or email match
  const searchByContact = async (phone, email) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (phone && phone.trim() !== '') {
        params.append('phone', phone.trim());
      }
      if (email && email.trim() !== '') {
        params.append('email', email.trim());
      }
      
      if (params.toString() === '') {
        return; // No search criteria
      }
      
      const { data } = await axios.get(`/diary/search-by-contact?${params.toString()}`);
      
      if (data.found && data.customer) {
        // Auto-populate all fields from the found customer
        setDiaryData(d => ({
          ...d,
          name: data.customer.customerName || d.name,
          email: data.customer.customerEmail || d.email,
          phone: data.customer.customerPhone || d.phone,
          from: data.customer.from || d.from,
          to: data.customer.to || d.to
        }));
        setIsContactAutoPopulated(true);
        toast.success(`Customer found: ${data.customer.customerName}`, {
          position: 'top-center',
          autoClose: 2000
        });
      } else {
        setIsContactAutoPopulated(false);
      }
    } catch (err) {
      console.error('Error searching by contact:', err);
      setIsContactAutoPopulated(false);
    }
  };

  const handleDiaryPhoneChange = v => setDiaryData(d => ({ ...d, phone: v || '' }));
  const handleDiaryPhone2Change = v => setDiaryData(d => ({ ...d, phone2: v || '' }));
  
  // Helper function to create input with inline delete icon
  const InputWithDelete = ({ value, onChange, onDelete, name, type = "text", placeholder = "", required = false, className = "", as = undefined, rows = undefined, maxLength = undefined, options = undefined, disabled = false, ...props }) => {
    return (
      <div className="position-relative">
        {as === 'textarea' ? (
          <Form.Control
            as={as}
            rows={rows}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            className={className}
            maxLength={maxLength}
            disabled={disabled}
            {...props}
          />
        ) : as === 'select' ? (
          <Form.Select
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className={className}
            disabled={disabled}
            size={props.size}
            style={{ paddingRight: value ? '35px' : undefined }}
            {...props}
          >
            {options}
          </Form.Select>
        ) : (
          <Form.Control
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            className={className}
            disabled={disabled}
            style={{ paddingRight: value ? '35px' : undefined }}
            {...props}
          />
        )}
        {value && (
          <button
            type="button"
            onClick={onDelete}
            className="position-absolute border-0 bg-transparent text-danger p-0"
            style={{ 
              cursor: 'pointer', 
              zIndex: 10, 
              top: as === 'textarea' ? '15px' : '50%', 
              right: '10px', 
              transform: as === 'textarea' ? 'none' : 'translateY(-50%)',
              lineHeight: 1
            }}
            title="Clear field"
          >
            <FaTimes />
          </button>
        )}
      </div>
    );
  };

  const handleDiarySubmit = async e => {
    e.preventDefault();
    if (!diaryData.phone || diaryData.phone.trim() === '') return setMsg('Enter a valid phone.');
    
    const iso = diaryData.dateTime.toISOString();
    if (new Date(iso) < new Date()) return setMsg('Date must be future.');
    
    try {
      setDiaryLoading(true);
      await axios.post('/diary/add-entry', {
        customerName: diaryData.name,
        customerEmail: diaryData.email,
        customerPhone: diaryData.phone,
        customerPhone2: diaryData.phone2 || null,
        from: diaryData.from,
        to: diaryData.to,
        dateTime: iso,
        passengers: diaryData.passengers,
        returnTrip: diaryData.returnTrip,
        dog: diaryData.dog || false,
        disabled: diaryData.disabled || false,
        notes: diaryData.notes || ''
      });
      
      await loadJobs();
      setShowAdd(false);
      setDiaryData({
        name: '', email: '', phone: '', phone2: '', from: '', to: '',
        dateTime: new Date(), passengers: '', returnTrip: false,
        dog: false, disabled: false, notes: ''
      });
      setShowPhone2(false);
      setMsg('Entry added successfully!');
    } catch (err) {
      console.error(err);
      setMsg('Failed to add entry');
    } finally {
      setDiaryLoading(false);
    }
  };

  const formatDateTime = (iso) => {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <Spinner animation="border" className="m-5" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Accepted Jobs</h2>
        <Button onClick={() => setShowAdd(true)} variant="success">
          Add to Diary
        </Button>
      </div>

      {msg && (
        <SimpleNotification
          message={msg}
          type={msg.includes('successfully') ? 'success' : 'error'}
          onClose={() => setMsg('')}
        />
      )}

      {jobs.length === 0 ? (
        <Alert variant="info">No accepted jobs found.</Alert>
      ) : (
        <div>
          {jobs.map((job, idx) => (
            <div
              key={job._id}
              className={`card mb-3 ${job.__available || job.status === 'pending' ? 'border-warning' : ''}`}
              style={job.__available || job.status === 'pending' ? { backgroundColor: '#fff8e1' } : undefined}
            >
              <div className="card-body">
                <h5 className="card-title">{job.customerName}</h5>
                <p className="card-text">
                  <strong>From:</strong> {job.from}<br/>
                  <strong>To:</strong> {job.to}<br/>
                  <strong>Date:</strong> {formatDateTime(job.dateTime)}<br/>
                  {job.customerPhone && (<><strong>Phone:</strong> {job.customerPhone}<br/></>)}
                  <strong>Email:</strong> {job.customerEmail}
                </p>

                {(job.__available || job.status === 'pending') ? (
                  <div className="d-flex flex-wrap gap-2">
                    <Button size="sm" variant="success" onClick={() => acceptLead(job._id)}>
                      Accept
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => declineLead(job._id)}>
                      Decline
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => respondLead(job._id, 'Available In 30Min')}>
                      30Min
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => respondLead(job._id, 'Available in 1hr')}>
                      1hr
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => respondLead(job._id, 'Available in 1.5hrs')}>
                      1.5hr
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add to Diary Modal */}
      <Modal show={showAdd} onHide={() => {clearAddJobForm(); setShowAdd(false);}} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add to Diary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleDiarySubmit}>
            {/* Phone Number - First Field */}
            <Form.Group className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label className="mb-0">Phone</Form.Label>
                {!showPhone2 && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setShowPhone2(true)}
                    className="p-0 text-primary"
                    title="Add second phone number"
                  >
                    <FaPlus /> Add Phone
                  </Button>
                )}
              </div>
              <div className="position-relative">
                <div style={{ position: 'relative' }}>
                  <SimplePhoneInput
                    value={diaryData.phone}
                    onChange={handleDiaryPhoneChange}
                    placeholder="Enter phone number"
                  />
                  {diaryData.phone && (
                    <button
                      type="button"
                      onClick={() => setDiaryData(d => ({ ...d, phone: '' }))}
                      className="position-absolute border-0 bg-transparent text-danger p-0"
                      style={{ 
                        cursor: 'pointer', 
                        zIndex: 10, 
                        top: '50%', 
                        right: '10px', 
                        transform: 'translateY(-50%)',
                        lineHeight: 1
                      }}
                      title="Clear field"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
            </Form.Group>

            {/* Second Phone Number Field */}
            {showPhone2 && (
              <Form.Group className="mb-3">
                <Form.Label>Phone 2</Form.Label>
                <div className="position-relative">
                  <div style={{ position: 'relative' }}>
                    <SimplePhoneInput
                      value={diaryData.phone2}
                      onChange={handleDiaryPhone2Change}
                      placeholder="Enter second phone number"
                    />
                    {diaryData.phone2 ? (
                      <button
                        type="button"
                        onClick={() => setDiaryData(d => ({ ...d, phone2: '' }))}
                        className="position-absolute border-0 bg-transparent text-danger p-0"
                        style={{ 
                          cursor: 'pointer', 
                          zIndex: 10, 
                          top: '50%', 
                          right: '10px', 
                          transform: 'translateY(-50%)',
                          lineHeight: 1
                        }}
                        title="Clear field"
                      >
                        <FaTimes />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowPhone2(false);
                          setDiaryData(d => ({ ...d, phone2: '' }));
                        }}
                        className="position-absolute border-0 bg-transparent text-danger p-0"
                        style={{ 
                          cursor: 'pointer', 
                          zIndex: 10, 
                          top: '50%', 
                          right: '10px', 
                          transform: 'translateY(-50%)',
                          lineHeight: 1
                        }}
                        title="Remove second phone field"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              </Form.Group>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      name="name"
                      value={diaryData.name}
                      onChange={handleDiaryChange}
                      placeholder="Enter name or #search"
                      required
                      className={isContactAutoPopulated ? 'border-success' : ''}
                      style={{ paddingRight: diaryData.name ? '35px' : undefined }}
                    />
                    {diaryData.name && (
                      <button
                        type="button"
                        onClick={() => setDiaryData(d => ({ ...d, name: '' }))}
                        className="position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-danger p-0"
                        style={{ cursor: 'pointer', zIndex: 15, right: searchLoading ? '35px' : '10px' }}
                        title="Clear field"
                      >
                        <FaTimes />
                      </button>
                    )}
                    {searchLoading && (
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2" style={{ right: diaryData.name ? '35px' : '10px' }}>
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                    {showSearchResults && searchResults.length > 0 && (
                      <div className="position-absolute w-100 bg-white border rounded shadow-sm" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto', top: '100%', marginTop: '2px' }}>
                        {searchResults.map((customer, index) => (
                          <div
                            key={index}
                            className="p-2 border-bottom cursor-pointer hover-bg-light"
                            style={{ cursor: 'pointer' }}
                            onClick={() => selectCustomer(customer)}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <div className="fw-bold small">{customer.customerName}</div>
                            <div className="text-muted small">{customer.customerPhone}</div>
                            <div className="text-muted small">{customer.from} → {customer.to}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {isContactAutoPopulated && (
                    <small className="text-success">Customer details auto-populated</small>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <InputWithDelete
                    type="email"
                    name="email"
                    value={diaryData.email}
                    onChange={handleDiaryChange}
                    onDelete={() => setDiaryData(d => ({ ...d, email: '' }))}
                    required
                    className={isContactAutoPopulated ? 'border-success' : ''}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Date & Time</Form.Label>
                  <InputWithDelete
                    type="datetime-local"
                    name="dateTime"
                    value={diaryData.dateTime.toISOString().slice(0, 16)}
                    onChange={e => setDiaryData(d => ({ ...d, dateTime: new Date(e.target.value) }))}
                    onDelete={() => setDiaryData(d => ({ ...d, dateTime: new Date() }))}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From</Form.Label>
                  <InputWithDelete
                    type="text"
                    name="from"
                    value={diaryData.from}
                    onChange={handleDiaryChange}
                    onDelete={() => setDiaryData(d => ({ ...d, from: '' }))}
                    required
                    className={isContactAutoPopulated ? 'border-success' : ''}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <Form.Label className="mb-0">To</Form.Label>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={swapFromTo}
                      title="Swap From and To"
                      style={{ 
                        width: '40px', 
                        height: '32px', 
                        padding: '0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '0.7',
                        fontSize: '22px',
                        fontWeight: 'bold'
                      }}
                    >
                      <div>⌃</div>
                      <div>⌄</div>
                    </Button>
                  </div>
                  <InputWithDelete
                    type="text"
                    name="to"
                    value={diaryData.to}
                    onChange={handleDiaryChange}
                    onDelete={() => setDiaryData(d => ({ ...d, to: '' }))}
                    required
                    className={isContactAutoPopulated ? 'border-success' : ''}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Group className="mb-1">
                  <Form.Label>Passengers</Form.Label>
                  <InputWithDelete
                    as="select"
                    name="passengers"
                    value={diaryData.passengers}
                    onChange={handleDiaryChange}
                    onDelete={() => setDiaryData(d => ({ ...d, passengers: '' }))}
                    required
                    size="sm"
                    options={
                      <>
                        <option value="">Select...</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                        <option value="8">8</option>
                      </>
                    }
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  name="dog"
                  checked={diaryData.dog}
                  onChange={handleDiaryChange}
                  label="🐕 Dog/Pet"
                />
              </Col>
              <Col md={3}>
                <Form.Group className="mb-1">
                  <Form.Label>Fare (£)</Form.Label>
                  <InputWithDelete
                    type="text"
                    name="fareAmount"
                    value={diaryData.fareAmount || ''}
                    onChange={handleDiaryChange}
                    onDelete={() => setDiaryData(d => ({ ...d, fareAmount: '' }))}
                    placeholder="e.g. £12.50"
                    size="sm"
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  name="disabled"
                  checked={diaryData.disabled}
                  onChange={handleDiaryChange}
                  label="♿ Disabled"
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  name="returnTrip"
                  checked={diaryData.returnTrip}
                  onChange={handleDiaryChange}
                  label="Return Trip"
                />
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <InputWithDelete
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={diaryData.notes}
                    onChange={handleDiaryChange}
                    onDelete={() => setDiaryData(d => ({ ...d, notes: '' }))}
                    maxLength={100}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="secondary" onClick={() => {clearAddJobForm(); setShowAdd(false);}}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={diaryLoading}>
                {diaryLoading ? <Spinner size="sm" /> : 'Add Entry'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
} 